import { createContext, useContext, useState, useLayoutEffect, ReactNode } from 'react'
import i18n from '../i18n'
import { orgParamForResolve } from '../utils/orgParam'

export interface ContractConfig {
  key: string // unique identifier, e.g. "treatment_contract"
  documentIndex: number // index in Yousign documents array
  consentType: string // maps to consent DB type
  downloadFilename: string // e.g. "Behandlungsvertrag.pdf"
  title: Record<string, string> // { de: "...", en: "...", ... }
  description: Record<string, string>
  downloadLabel: Record<string, string>
}

/**
 * Which payment step an org shows. One value replaces the old pair of
 * `skipSepa` + `cardSetup`, which could contradict each other.
 * Mirrors server/src/services/paymentStep.ts.
 */
export type PaymentStep = 'none' | 'sepa_test' | 'sepa' | 'card_test' | 'card'

/**
 * Resolve the payment step, tolerating a backend that predates the field —
 * frontend and backend deploy independently, so during a rollout the config
 * may still arrive with only the legacy pair set.
 */
export function resolvePaymentStep(tenant: TenantConfig | null): PaymentStep {
  if (tenant?.paymentStep) return tenant.paymentStep
  if (tenant?.cardSetup === 'on') return 'card'
  if (tenant?.cardSetup === 'test') return 'card_test'
  return tenant?.skipSepa ? 'none' : 'sepa'
}

export interface TenantConfig {
  slug: string
  name: string
  country: string
  locale: string // "de", "en", "it", "es"
  defaultCountry: string // preselected country in forms
  phonePrefix: string // e.g. "+49", "+43", "+39"
  contracts: ContractConfig[] | null // signing step contracts
  patientNumberDigits: number // digits after "P" (5 → P12345, 6 → P123456)
  /** Which payment step this org shows — the single source of truth. */
  paymentStep?: PaymentStep
  skipSepa: boolean // derived from paymentStep; kept for the standalone /sepa page
  sepaRedirectUrl: string | null // per-org SEPA redirect URL
  addressValidation?: 'off' | 'test' | 'on' // address autocomplete/verification feature mode
  sepaDropin?: 'off' | 'test' | 'ab' | 'on' // how the SEPA step collects the mandate (drop-in vs redirect; ab = 50/50)
  sepaOptOutVariant?: 'off' | 'test' | 'ab' | 'on' // two-step SEPA opt-out (src/flow/sepaExperiments.ts)
  supportWhatsappUrl?: string | null // WhatsApp support deep link (card payment step)
  requirePatientToken?: boolean // only patients with a magic-link session may use the form
  cardSetup?: 'off' | 'test' | 'on'  // derived from paymentStep; kept for backward compat
  logoUrl: string | null
  primaryColor: string | null
  companyName: string | null
  websiteUrl: string | null
  imprintUrl: string | null
  privacyUrl: string | null
  termsUrl: string | null
  contactEmail: string | null
  socialLinks: { platform: string; handle: string; url: string }[] | null
}

interface TenantContextValue {
  tenant: TenantConfig | null
  loading: boolean
  error: string | null
}

const TenantContext = createContext<TenantContextValue>({
  tenant: null,
  loading: true,
  error: null,
})

// Module-level storage for the tenant (accessible outside React)
let currentTenantSlug: string | null = null
let currentTenantConfig: TenantConfig | null = null

export function getTenantSlug(): string | null {
  return currentTenantSlug
}

export function getTenantConfig(): TenantConfig | null {
  return currentTenantConfig
}

// The org resolution in flight. Patient API requests wait for it (services/api.ts): without
// X-Tenant-ID the server takes a request for 'de'. The form's boot requests and a reloaded
// completion page went out before the org was known (2026-11-03).
let tenantResolution: Promise<void> | null = null

/** TenantProvider registers its resolution here; `whenTenantResolved` waits for it. */
export function trackTenantResolution(resolution: Promise<void>): void {
  tenantResolution = resolution
}

/** Resolves once the org is known or its resolution gave up, after at most `timeoutMs`. Never rejects. */
export function whenTenantResolved(timeoutMs = 8000): Promise<void> {
  const pending = tenantResolution
  if (currentTenantSlug || !pending) return Promise.resolve()
  return new Promise<void>(resolve => {
    const timer = setTimeout(resolve, timeoutMs)
    pending.then(() => { clearTimeout(timer); resolve() }, () => { clearTimeout(timer); resolve() })
  })
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<TenantConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // A layout effect: every layout effect of a commit runs before any passive one, so the
  // resolution is under way before the pages' useEffect requests ask for it.
  useLayoutEffect(() => {
    const resolveTenant = async () => {
      try {
        // Determine domain to resolve
        const hostname = window.location.hostname

        // Dev fallback: use ?org= query param
        const urlParams = new URLSearchParams(window.location.search)
        const orgParam = urlParams.get('org')

        const API_URL = import.meta.env.VITE_API_URL || '/api'
        // The GAM platform may name the org by its own slug (gam-italy → it); patient links never do
        const domain = orgParamForResolve(orgParam) || hostname

        const response = await fetch(`${API_URL}/tenants/resolve?domain=${encodeURIComponent(domain)}`)

        if (!response.ok) {
          // If domain not found, try localhost fallback in dev
          if (hostname === 'localhost' || hostname === '127.0.0.1') {
            const fallbackResponse = await fetch(`${API_URL}/tenants/resolve?domain=localhost`)
            if (fallbackResponse.ok) {
              const data = await fallbackResponse.json()
              const config = data.data as TenantConfig
              currentTenantSlug = config.slug
              currentTenantConfig = config
              setTenant(config)
              applyBranding(config)
              setLoading(false)
              return
            }
          }
          setError(`No organization found for this domain: ${hostname}`)
          setLoading(false)
          return
        }

        const data = await response.json()
        const config = data.data as TenantConfig
        currentTenantSlug = config.slug
        currentTenantConfig = config
        setTenant(config)
        applyBranding(config)
      } catch {
        // Error intentionally not logged to console for patients
        // Fail open: use defaults so patients aren't blocked
        currentTenantSlug = 'de'
        const fallbackConfig: TenantConfig = {
          slug: 'de',
          name: 'GAM Medical',
          country: 'DE',
          locale: 'de',
          defaultCountry: 'Germany',
          phonePrefix: '+49',
          contracts: null, // will use hardcoded defaults
          patientNumberDigits: 5,
          paymentStep: 'sepa',
          skipSepa: false,
          sepaRedirectUrl: null,
          logoUrl: null,
          primaryColor: null,
          companyName: 'GAM Medical',
          websiteUrl: 'https://gam-medical.de',
          imprintUrl: 'https://gam-medical.de/impressum/',
          privacyUrl: 'https://gam-medical.de/datenschutzbestimmungen/',
          termsUrl: 'https://gam-medical.de/agb/',
          contactEmail: 'info@gam-medical.de',
          socialLinks: null,
        }
        currentTenantConfig = fallbackConfig
        setTenant(fallbackConfig)
      } finally {
        setLoading(false)
      }
    }

    trackTenantResolution(resolveTenant())
  }, [])

  return (
    <TenantContext.Provider value={{ tenant, loading, error }}>
      {children}
    </TenantContext.Provider>
  )
}

export function useTenant() {
  return useContext(TenantContext)
}

/**
 * Apply org-specific branding and language.
 */
const SUPPORTED_LANGUAGES = ['de', 'en', 'it', 'es'] as const

/** A `?language=` (or `?lang=`) URL param overrides the org's default locale,
 *  so a patient (or a reviewer) can open the form in another supported
 *  language without changing org config. Falls back to the org locale. */
function resolveInitialLanguage(orgLocale: string | undefined): string {
  try {
    const params = new URLSearchParams(window.location.search)
    const requested = (params.get('language') || params.get('lang') || '').toLowerCase()
    if ((SUPPORTED_LANGUAGES as readonly string[]).includes(requested)) return requested
  } catch {
    // ignore — fall through to org locale
  }
  return orgLocale || 'de'
}

function applyBranding(config: TenantConfig) {
  // Set the UI language: URL ?language= wins, else the org's locale.
  const lang = resolveInitialLanguage(config.locale)
  if (lang && i18n.language !== lang) {
    i18n.changeLanguage(lang)
  }
  const root = document.documentElement

  if (config.primaryColor) {
    root.style.setProperty('--color-primary', config.primaryColor)
    // Darken for hover state
    root.style.setProperty('--color-primary-hover', darkenColor(config.primaryColor, 15))
  }

  // Update page title
  if (config.companyName) {
    const word = { de: 'Patientenregistrierung', it: 'Registrazione pazienti', es: 'Registro de pacientes', en: 'Patient registration' }[config.locale] ?? 'Patient registration'
    document.title = `${config.companyName} - ${word}`
  }
}

/**
 * Simple color darkening utility.
 */
function darkenColor(hex: string, percent: number): string {
  if (!/^#?[0-9a-fA-F]{6}$/.test(hex)) return hex // malformed config → leave color untouched
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.max(0, (num >> 16) - Math.round(2.55 * percent))
  const g = Math.max(0, ((num >> 8) & 0x00ff) - Math.round(2.55 * percent))
  const b = Math.max(0, (num & 0x0000ff) - Math.round(2.55 * percent))
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`
}
